/**
 * Employee IT Access Tab Component
 * 
 * This component manages IT access credentials for employees.
 * Extracted from EmployeeForm.jsx for modularity.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { 
  Key, Monitor, Plus, Pencil, Trash2, X, Check, Copy, Eye, EyeOff, ExternalLink
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { copyToClipboard } from './helpers';
import { Favicon } from '../../components/Favicon';
import { openLoginAndCopy } from '../../lib/itAccessHelpers';
import { usePasswordReveal } from '../../hooks/usePasswordReveal';
import { RevealCountdownIcon } from '../../components/RevealCountdownIcon';

const ITAccessTab = ({
  formData,
  // Presets
  itPresets,
  presetDialogOpen,
  setPresetDialogOpen,
  newPreset,
  setNewPreset,
  editingPreset,
  setEditingPreset,
  handleSavePreset,
  handleDeletePreset,
  handleAddFromPreset,
  // IT Accesses
  itAccesses,
  loadingIT,
  newITAccess,
  setNewITAccess,
  editingITAccess,
  setEditingITAccess,
  handleAddITAccess,
  handleUpdateITAccess,
  handleDeleteITAccess,
}) => {
  const reveal = usePasswordReveal({ totalMs: 10000 });
  return (
    <div className="space-y-6">
      {/* Presets */}
      <Card className="border-border/60">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <Key className="h-5 w-5" />
              Vorlagen (Presets)
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Klicken Sie auf eine Vorlage, um sie für diesen Mitarbeiter anzuwenden
            </p>
          </div>
          <Button 
            type="button"
            variant="outline" 
            size="sm"
            onClick={() => {
              setEditingPreset(null);
              setNewPreset({ name: '', program: '', default_login_template: '', info: '', color: '#6B7280' });
              setPresetDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Neue Vorlage
          </Button>
        </CardHeader>
        <CardContent>
          {itPresets.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Keine Vorlagen vorhanden. Erstellen Sie eine Vorlage für häufig verwendete Programme.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {itPresets.map(preset => (
                <div key={preset.id} className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddFromPreset(preset.id)}
                    className="gap-2"
                    style={{ borderColor: preset.color, color: preset.color }}
                  >
                    <Monitor className="h-3.5 w-3.5" />
                    {preset.name}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => {
                      setEditingPreset(preset);
                      setNewPreset({
                        name: preset.name,
                        program: preset.program,
                        default_login_template: preset.default_login_template || '',
                        info: preset.info || '',
                        url: preset.url || '',
                        color: preset.color,
                      });
                      setPresetDialogOpen(true);
                    }}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Preset Dialog */}
          {presetDialogOpen && (
            <div className="mt-4 p-4 border rounded-lg bg-muted/30 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">{editingPreset ? 'Vorlage bearbeiten' : 'Neue Vorlage'}</h4>
                <Button type="button" variant="ghost" size="icon" onClick={() => setPresetDialogOpen(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={newPreset.name || ''}
                    onChange={(e) => setNewPreset({ ...newPreset, name: e.target.value })}
                    placeholder="z.B. Microsoft 365"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Programm *</Label>
                  <Input
                    value={newPreset.program || ''}
                    onChange={(e) => setNewPreset({ ...newPreset, program: e.target.value })}
                    placeholder="z.B. Office 365, SAP, etc."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Login-Vorlage</Label>
                  <Input
                    value={newPreset.default_login_template || ''}
                    onChange={(e) => setNewPreset({ ...newPreset, default_login_template: e.target.value })}
                    placeholder="{vorname}.{nachname}"
                  />
                  <p className="text-xs text-muted-foreground">
                    Verwenden Sie {'{vorname}'} und {'{nachname}'} als Platzhalter
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Farbe</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={newPreset.color || ''}
                      onChange={(e) => setNewPreset({ ...newPreset, color: e.target.value })}
                      className="w-10 h-10 rounded cursor-pointer border"
                    />
                    <Input
                      value={newPreset.color || ''}
                      onChange={(e) => setNewPreset({ ...newPreset, color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Login-URL (optional)</Label>
                <Input
                  type="url"
                  value={newPreset.url || ''}
                  onChange={(e) => setNewPreset({ ...newPreset, url: e.target.value })}
                  placeholder="https://login.example.com"
                />
                <p className="text-xs text-muted-foreground">
                  Direktlink zur Login-Seite des Programms — wird beim Anlegen aus der Vorlage übernommen.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Info (optional)</Label>
                <Input
                  value={newPreset.info || ''}
                  onChange={(e) => setNewPreset({ ...newPreset, info: e.target.value })}
                  placeholder="Zusätzliche Informationen..."
                />
              </div>
              <div className="flex justify-between">
                {editingPreset && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      handleDeletePreset(editingPreset.id);
                      setPresetDialogOpen(false);
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Löschen
                  </Button>
                )}
                <div className="flex gap-2 ml-auto">
                  <Button type="button" variant="outline" size="sm" onClick={() => setPresetDialogOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button type="button" size="sm" onClick={handleSavePreset}>
                    Speichern
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* IT-Zugänge */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            IT-Zugänge für {formData.first_name} {formData.last_name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingIT ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Tabelle */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium">Programm</th>
                      <th className="text-left p-3 font-medium">Login</th>
                      <th className="text-left p-3 font-medium">Passwort</th>
                      <th className="text-left p-3 font-medium">Info</th>
                      <th className="text-right p-3 font-medium w-24">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itAccesses.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-muted-foreground">
                          Keine IT-Zugänge vorhanden. Fügen Sie einen neuen Zugang hinzu oder verwenden Sie eine Vorlage.
                        </td>
                      </tr>
                    ) : (
                      itAccesses.map(access => (
                        <tr key={access.id} className="border-t">
                          {editingITAccess?.id === access.id ? (
                            <>
                              <td className="p-2">
                                <Input
                                  value={editingITAccess.program || ''}
                                  onChange={(e) => setEditingITAccess({ ...editingITAccess, program: e.target.value })}
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  value={editingITAccess.login || ''}
                                  onChange={(e) => setEditingITAccess({ ...editingITAccess, login: e.target.value })}
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  type="text"
                                  value={editingITAccess.password || ''}
                                  onChange={(e) => setEditingITAccess({ ...editingITAccess, password: e.target.value })}
                                  placeholder={editingITAccess.has_password ? 'Leer = unverändert' : 'Neues Passwort'}
                                  title="Leer lassen, um das vorhandene Passwort nicht zu überschreiben."
                                />
                              </td>
                              <td className="p-2">
                                <Input
                                  value={editingITAccess.info || ''}
                                  onChange={(e) => setEditingITAccess({ ...editingITAccess, info: e.target.value })}
                                />
                              </td>
                              <td className="p-2 text-right">
                                <div className="flex justify-end gap-1">
                                  <Button type="button" size="icon" variant="ghost" onClick={handleUpdateITAccess}>
                                    <Check className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button type="button" size="icon" variant="ghost" onClick={() => setEditingITAccess(null)}>
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="p-3 font-medium">
                                <div className="flex items-center gap-2">
                                  <Favicon url={access.url} size={20} />
                                  <div className="min-w-0">
                                    <div className="truncate">{access.program}</div>
                                    {access.url && (
                                      <a
                                        href={access.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[11px] text-primary hover:underline truncate block"
                                      >
                                        {access.url}
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="p-3">
                                {access.login ? (
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-sm">{access.login}</span>
                                    <Button 
                                      type="button"
                                      size="icon" 
                                      variant="ghost" 
                                      className="h-6 w-6"
                                      onClick={() => copyToClipboard(access.login)}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="p-3">
                                {access.has_password ? (
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-sm text-muted-foreground italic" title="Passwort ist gesetzt — nur der Mitarbeiter selbst kann es einsehen.">
                                      ••••••••
                                    </span>
                                    <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-emerald-700 border-emerald-300 bg-emerald-50">
                                      gesetzt
                                    </Badge>
                                  </div>
                                ) : access.password ? (
                                  // Eigenes Profil — Klartext sichtbar
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-sm">
                                      {reveal.isVisible(access.id) ? access.password : '••••••••'}
                                    </span>
                                    <Button
                                      type="button"
                                      size="icon"
                                      variant="ghost"
                                      className="h-6 w-6"
                                      onClick={() => reveal.toggle(access.id)}
                                      title={reveal.isVisible(access.id) ? 'Ausblenden (Auto-Hide nach 10 s)' : 'Anzeigen (10 s)'}
                                    >
                                      <RevealCountdownIcon
                                        visible={reveal.isVisible(access.id)}
                                        remainingPct={reveal.remainingPct(access.id)}
                                        size={14}
                                      />
                                    </Button>
                                    <Button
                                      type="button"
                                      size="icon"
                                      variant="ghost"
                                      className="h-6 w-6"
                                      onClick={() => copyToClipboard(access.password)}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="p-3 text-sm text-muted-foreground">{access.info || '-'}</td>
                              <td className="p-3 text-right">
                                <div className="flex justify-end gap-1">
                                  {access.url && (
                                    <Button
                                      type="button"
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => openLoginAndCopy({ url: access.url, login: access.login })}
                                      title={access.login ? 'Login-Seite oeffnen & Login kopieren' : 'Login-Seite oeffnen'}
                                      data-testid={`open-it-access-${access.id}`}
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <Button 
                                    type="button"
                                    size="icon" 
                                    variant="ghost"
                                    onClick={() => setEditingITAccess(access)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button 
                                    type="button"
                                    size="icon" 
                                    variant="ghost"
                                    onClick={() => handleDeleteITAccess(access.id)}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Neuen Zugang hinzufügen */}
              <div className="border rounded-lg p-4 bg-muted/20">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Neuen IT-Zugang hinzufügen
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Programm *</Label>
                    <Input
                      value={newITAccess.program || ''}
                      onChange={(e) => setNewITAccess({ ...newITAccess, program: e.target.value })}
                      placeholder="z.B. SAP"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Login</Label>
                    <Input
                      value={newITAccess.login || ''}
                      onChange={(e) => setNewITAccess({ ...newITAccess, login: e.target.value })}
                      placeholder="Benutzername"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Passwort</Label>
                    <Input
                      value={newITAccess.password || ''}
                      onChange={(e) => setNewITAccess({ ...newITAccess, password: e.target.value })}
                      placeholder="Passwort"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Info</Label>
                    <Input
                      value={newITAccess.info || ''}
                      onChange={(e) => setNewITAccess({ ...newITAccess, info: e.target.value })}
                      placeholder="Zusatzinfo"
                    />
                  </div>
                </div>
                <Button type="button" className="mt-3" size="sm" onClick={handleAddITAccess}>
                  <Plus className="h-4 w-4 mr-2" />
                  Hinzufügen
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ITAccessTab;
